// utils/pdfExport.ts
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export function generateDeliveryPdf(address: any, rows: any[], order_info?: any) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;

  // --- Format current date and time ---
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const formattedDate = `${day}-${month}-${year}`;
  const formattedTime = `${hours}:${minutes}`;

  // --- Calculate header height dynamically ---
  const calculateHeaderHeight = () => {
    let headerHeight = 30; // Title
    if (order_info?.order_no) {
      headerHeight += 27; // Order number space
    } else {
      headerHeight += 15; // Smaller gap if no order number
    }
    headerHeight += 15; // "Shipment address" label

    // Count address lines
    const addr = [
      address.name,
      address.address1,
      address.address2,
      `${address.postalCode} ${address.city}`,
      address.country,
    ].filter(Boolean);

    headerHeight += addr.length * 12; // Each address line
    headerHeight += 20; // Bottom padding

    return headerHeight;
  };

  const headerHeight = calculateHeaderHeight();

  // --- Group rows by line_id WHILE PRESERVING ORDER ---
  const groups = [];
  const processedLineIds = new Set();

  for (const row of rows) {
    if (!processedLineIds.has(row.line_id)) {
      processedLineIds.add(row.line_id);
      // Collect all rows with this line_id in the order they appear
      const groupRows = rows.filter(r => r.line_id === row.line_id);
      groups.push({
        line_id: row.line_id,
        rows: groupRows
      });
    }
  }

  const body: any[] = [];
  const notIncludedSummary: string[] = [];
  const outstandingSummary: string[] = [];

  // Process groups in order
  groups.forEach(({ rows: groupRows }) => {
    const first = groupRows[0];
    const qtyOrdered = first.qty_ordered || 0;
    const totalToShip = groupRows.reduce((s, r) => s + (r.qty_to_ship || 0), 0);
    const outstanding = Math.max(qtyOrdered - totalToShip, 0);

    const allNotIncluded = groupRows.every(r => r.not_included);
    const allZeroShip = groupRows.every(r => (r.qty_to_ship || 0) === 0);

    // If all are not included OR all have zero qty_to_ship → skip table, mark as not included
    if (allNotIncluded || allZeroShip) {
      notIncludedSummary.push(
        `• ${first.product_name}${first.size ? " - " + first.size : ""} ${
          first.sku_number ? `(${first.sku_number})` : ""
        } - Qty: ${qtyOrdered}`
      );
      return;
    }

    // Filter out child rows with qty_to_ship = 0 (they're considered outstanding)
    const shippedRows = groupRows.filter(r => (r.qty_to_ship || 0) > 0 && !r.not_included);

    // Skip if no shipped rows remain
    if (!shippedRows.length) {
      if (outstanding > 0) {
        outstandingSummary.push(
          `• ${first.product_name}${first.size ? " - " + first.size : ""} ${
            first.sku_number ? `(${first.sku_number})` : ""
          } - Qty: ${outstanding}`
        );
      }
      return;
    }

    // Format box label with box_id
    const formatBoxLabel = (box: any) => {
      if (!box) return "";
      if (box.box_id) {
        return `${box.label} (${box.box_id})`;
      }
      return box.label || "";
    };

    // Add first (main) row to table
    body.push([
      first?.sku_number,
      `${first?.product_name}`,
      `${first.name ?? ""}${first?.name && first?.number ? " - " : ""}${first?.number ?? ""}`,
      first?.size,
      qtyOrdered,
      totalToShip,
      outstanding,
      formatBoxLabel(first?.box),
    ]);

    // Add only shipped child rows (skip zero-qty ones)
    for (let i = 1; i < shippedRows.length; i++) {
      const r = shippedRows[i];
      body.push([
        "",
        "",
        "",
        "",
        "",
        r.qty_to_ship || "",
        "",
        formatBoxLabel(r.box),
      ]);
    }

    // Add to outstanding summary if some quantity is left
    if (outstanding > 0) {
      outstandingSummary.push(
        `• ${first.product_name}${first.size ? " - " + first.size : ""} ${
          first.sku_number ? `(${first.sku_number})` : ""
        } - Qty: ${outstanding}`
      );
    }
  });

  // --- Header drawn on each page ---
  const drawHeader = () => {
    // Left side - Title
    doc.setFontSize(16).setFont("helvetica", "bold");
    doc.text("Delivery Note", margin, 30);

    if (order_info?.order_no) {
      doc.setFontSize(10).setFont("helvetica", "bold");
      doc.text(`Order #: ${order_info.order_no}`, margin, 50);
    }

    // Right side - Date, Time
    doc.setFontSize(10).setFont("helvetica", "bold");
    const rightX = pageWidth - margin;
    doc.text(`Date: ${formattedDate}`, rightX, 30, { align: "right" });
    doc.text(`Time: ${formattedTime}`, rightX, 42, { align: "right" });

    // Shipment address
    let yPos = order_info?.order_no ? 70 : 60;
    doc.setFontSize(10).setFont("helvetica", "bold");
    doc.text("Shipment address", margin, yPos);

    const addr = [
      address.name,
      address.address1,
      address.address2,
      `${address.postalCode} ${address.city}`,
      address.country,
    ].filter(Boolean);

    doc.setFont("helvetica", "normal");
    yPos += 16;
    addr.forEach((line) => {
      doc.text(line, margin, yPos);
      yPos += 12;
    });
  };

  // Draw initial header
  drawHeader();

  // --- Table with borders only between rows ---
  autoTable(doc, {
    startY: headerHeight + 20, // Start below header with padding
    head: [["SKU", "Product Name", 'Name & No.', "Size", "Qty ordered", "Qty to ship", "Outstanding", "Box"]],
    body,
    styles: {
      fontSize: 10,
      cellPadding: 3,
      halign: "center",
    },
    headStyles: {
      fillColor: [60, 60, 60],
      textColor: 255,
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    columnStyles: { 0: { halign: "left" }, 1: { halign: "left" } },
    margin: {
      left: margin,
      right: margin,
      top: headerHeight + 20, // Top margin for new pages
      bottom: 60
    },
    didDrawCell: (data) => {
      // Draw border only at the top of each cell (horizontal lines between rows)
      if (data.section === 'body') {
        const { cell, cursor } = data;
        doc.setDrawColor(60, 60, 60);
        doc.setLineWidth(0.5);
        doc.line(
          cursor.x,
          cursor.y,
          cursor.x + cell.width,
          cursor.y
        );
      }
    },
    didDrawPage: (data) => {
      // Draw header on each new page
      if (data.pageNumber > 1) {
        drawHeader();
      }

      // Footer with page number (temporary, will be updated later)
      doc.setFontSize(9).setFont("helvetica", "normal");
      doc.text(`Page ${data.pageNumber}`, pageWidth - margin - 60, pageHeight - 20);
    },
  });

  let finalY = (doc as any).lastAutoTable.finalY + 20;

  // --- Render summaries with proper paging ---
  const renderList = (title: string, lines: string[]) => {
    if (!lines.length) return;

    // Check if we need a new page for the title
    if (finalY > pageHeight - 100) {
      doc.addPage();
      drawHeader();
      finalY = headerHeight + 30;
    }

    doc.setFont("helvetica", "bold").setFontSize(15);
    doc.text(title, margin, finalY);
    finalY += 16;

    doc.setFont("helvetica", "normal").setFontSize(9);
    lines.forEach(line => {
      // Check if we need a new page for content
      if (finalY > pageHeight - 60) {
        doc.addPage();
        drawHeader();
        finalY = headerHeight + 30;
      }
      doc.text(line, margin + 10, finalY);
      finalY += 12;
    });

    finalY += 20;
  };

  renderList("Items Not Included in this shipment:", notIncludedSummary);
  renderList("Outstanding Items:", outstandingSummary);

  // --- Finalize page numbers ---
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(9).setFont("helvetica", "normal");
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 60, pageHeight - 20);
  }

  return doc;
}

// Attach Ctrl+P shortcut
export function enableDeliveryPdfShortcut(address: any, rows: any[], order_info?: any): void {
    const handler = (e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
            e.preventDefault();
            const doc = generateDeliveryPdf(address, rows, order_info);
            doc.autoPrint();
            window.open(doc.output("bloburl"), "_blank");
        }
    };

    window.removeEventListener("keydown", handler); // ensure only one
    window.addEventListener("keydown", handler);
}

/**
 * Sorts an array of rows by box info, then factory_product_id, then line_id.
 * @param rows - Array of rows to sort
 * @param assignmentOrder - Optional Map of box identifier to order
 * @returns Sorted array of rows
 */
export function sortRowsByBox(
  rows: any[],
  boxAssignmentOrder?: Map<string, number>
): any[] {
  // Return a new sorted array (do not mutate original)
  return [...rows].sort((a, b) => {
    // Helper: combined box identifier
    const combined = (row: any) => {
      const label = row.box?.label || '';
      const id = row.box?.box_id || '';
      return label && id ? `${label} (${id})` : '';
    };

    const combinedA = combined(a);
    const combinedB = combined(b);

    // Sort by combined box identifier first
    if (combinedA !== combinedB) {
      if (!combinedA) return 1; // empty boxes last
      if (!combinedB) return -1;

      if (boxAssignmentOrder) {
        const orderA = boxAssignmentOrder.get(combinedA) ?? Infinity;
        const orderB = boxAssignmentOrder.get(combinedB) ?? Infinity;
        if (orderA !== orderB) return orderA - orderB;
      }

      // Fallback string comparison
      return combinedA.localeCompare(combinedB);
    }

    // Sort by factory_product_id
    if (a.factory_product_id !== b.factory_product_id) {
      return String(a.factory_product_id).localeCompare(String(b.factory_product_id));
    }

    // Sort by line_id as last resort
    return a.line_id - b.line_id;
  });
}
